'use client'

import { useEffect, useRef, useState } from 'react'
import { motion } from 'motion/react'

interface AnimatedTextProps {
  text: string
  className?: string
  classNameText?: string
  once?: boolean
  delay?: number
  type: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p'
  duration?: number
  variant?: 'default' | 'simple'
}

export default function AnimatedText({
  text,
  className = '',
  classNameText = '',
  type = 'h2',
  once = true,
  delay = 0,
  variant = 'default',
}: AnimatedTextProps) {
  const [isInView, setIsInView] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting) {
          setIsInView(true)
          if (once) observer.disconnect()
        } else if (!once) {
          setIsInView(false)
        }
      },
      { threshold: 0.1 },
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current)
      }
    }
  }, [once])

  if (!text) return null
  const words = text.split(' ')

  // Simplified container variants for better performance
  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
    },
  }

  // Simplified child variants with shorter duration
  const child = {
    visible: {
      opacity: 1,
      y: 0,
    },
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
    hidden: {
      opacity: 0,
      y: 5, // Reduced from 10 for faster animation
    },
  }

  // Simplified line variants
  const line = {
    visible: {
      width: '100%',
    },
    hidden: {
      width: '0%',
    },
  }

  if (!text) return null

  const Tag = type
  const content = (
    <Tag className="font-heading break-anywhere flex w-full max-w-5xl flex-wrap justify-center gap-1 hyphens-auto break-words critical-text">
      {words.map((word, index) => (
        <motion.span
          key={index}
          variants={child}
          className={`break-anywhere mr-1 inline-block hyphens-auto break-words ${classNameText}`}
        >
          {word}
        </motion.span>
      ))}
    </Tag>
  )

  if (variant === 'simple') {
    return (
      <motion.div
        ref={ref}
        className={`${className} flex justify-center overflow-hidden lcp-optimized`}
        variants={container}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
      >
        {content}
      </motion.div>
    )
  }

  return (
    <motion.div
      ref={ref}
      className={`${className} flex items-center gap-4 max-w-screen overflow-hidden lcp-optimized`}
      variants={container}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="flex w-1/3 justify-center md:w-1/2">
        <motion.div className="h-px w-1/4 bg-antonie-red/30 md:w-1/2" variants={line} />
      </div>
      {content}
      <div className="flex w-1/3 justify-center md:w-1/2">
        <motion.div className="h-px w-1/4 bg-antonie-red/30 md:w-1/2" variants={line} />
      </div>
    </motion.div>
  )
}
